/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { first, ReplaySubject, Subject } from "rxjs";
import {
    ButtonComponent, FormActionsComponent, FormComponent, FormInputComponent, FormPasswordInputComponent, requiredValidator, SpinnerComponent } from "typedb-cloud-framework";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { SnackbarService } from "../../../service/snackbar.service";
import { QRCodeComponent } from "angularx-qrcode";
import { MFASetupSession } from "../../../service/authentication/google-identity-backend.service";
import { IdentityAuthService } from "../../../service/authentication/identity-auth.service";
import { ReauthenticateComponent, ReauthStage } from "../../../framework/reauthenticate/reauthenticate.component";

const AUTHENTICATION_FAILED = "Authentication failed, please try again";

@Component({
    selector: "tp-mfa-setup",
    templateUrl: "./mfa-setup.component.html",
    styleUrl: "./mfa-setup.component.scss",
    standalone: true,
    imports: [
        AsyncPipe, FormInputComponent, FormActionsComponent, FormsModule, ReactiveFormsModule, ButtonComponent,
        FormComponent, MatFormFieldModule, MatInputModule, QRCodeComponent, SpinnerComponent, ReauthenticateComponent
    ],
})
export class MfaSetupComponent {

    loading = true;

    readonly reauthForm = this.formBuilder.nonNullable.group({
        email: ["", [requiredValidator]],
        password: ["", [requiredValidator]],
    });
    reauthRequired = false;

    readonly otpForm = this.formBuilder.nonNullable.group({
        mfaCode: ["", [requiredValidator]],
    });
    readonly mfaSession$ = new ReplaySubject<MFASetupSession>(1);
    showSecretKey = false;
    userEmail$ = this.auth.currentUserEmail$;

    @Input() isSubmitting$ = new Subject<boolean>()
    @Input() cancellable: boolean = true;
    @Output() cancel = new EventEmitter<void>();
    @Output() mfaSuccess = new EventEmitter<void>();

    constructor(private formBuilder: FormBuilder, private snackbar: SnackbarService, private auth: IdentityAuthService) {
        this.auth.startMFASetupSession().pipe(first()).subscribe({
            next: (session) => {
                this.loading = false;
                this.mfaSession$.next(session)
            },
            error: (err) => {
                this.loading = false;
                if (err?.code === 'auth/requires-recent-login') this.reauthRequired = true;
            }
        });

        this.auth.currentUserStateChanges.pipe(first(user => !!user)).subscribe(user => {
            if (!this.reauthForm.value.email && !!user.email) this.reauthForm.patchValue({ email: user.email });
        });
    }

    reauthStageChanged(stage: ReauthStage) {
        if (stage === "complete") {
            this.auth.startMFASetupSession().pipe(first()).subscribe(session => this.mfaSession$.next(session));
            this.reauthRequired = false;
        }
    }

    submitOTP() {
        this.mfaSession$.subscribe((session) => {
            session.enroll("TOTP", this.otpForm.value.mfaCode!)
                .then(() => {
                    this.snackbar.success("MFA method successfully added");
                    this.mfaSuccess.emit();
                })
                .catch((err) => {
                    this.isSubmitting$.next(false);
                    if (err?.code === "auth/invalid-verification-code") this.snackbar.errorPersistent(
                        "Invalid MFA verification code. Please try again."
                    );
                    else this.snackbar.errorPersistent(err?.message || err?.toString() || AUTHENTICATION_FAILED);
                });
        });
    }
}
